import { Fragment, useEffect, useMemo, useState } from 'react';

import type { IconList } from '@snowit/icetypes';
import type { MainRoute, NavGroup, NavRoute, RouteGroup } from '@snowit/wouter';

import filterMenu from '@snowit/components/Sidebar/Menu/filterMenu';
import useBookmarks from '@snowit/hookspannello/useBookmarks';
import useAuth from '@snowit/system/useAuth';
import useLanguage from '@snowit/system/useLanguage';
import { Icon } from '@snowit/ui';
import { m } from '@snowit/ui/utils/Motion';
import testId from '@snowit/utils/testId';
import { navGroups, useRouter } from '@snowit/wouter';

import cx from 'classnames';
import s from './Menu.module.scss';

import { AnimatePresence } from 'framer-motion';

type MenuProps = {
  className?: string;
  shrunk?: boolean;
  sidebarOnClick?: () => void;
};

const Menu = (props: MenuProps) => {
  const { className, shrunk, sidebarOnClick } = props;
  const { location, navigate_raw } = useRouter();
  const { authType, permissions } = useAuth();
  const { viewOnlyBookmarks, bookmarks } = useBookmarks();
  const [opened, setOpened] = useState<Record<RouteGroup, boolean>>({
    home: false,
    charts: false,
    admin_tools: false,
    catalog: false,
    changelogs: false,
    dealers: false,
    misc: false,
    orders: false,
    providers: false,
    settings: false,
    users: false,
    cards: false,
    lep: false,
    promos: false,
    search: false,
  });
  const { t } = useLanguage();

  const activeGroup: RouteGroup | undefined = useMemo(() => {
    if (location === '/') return undefined;
    return navGroups.find((group) => {
      return group.routes.find((route) => {
        if (route.path === ('/' as `/${MainRoute}`)) return false;
        return location.includes(route.path);
        // return false;
      });
    })?.id;
  }, [location]);

  useEffect(() => {
    if (activeGroup) {
      setOpened((prev) => ({ ...prev, [activeGroup]: true }));
    }
  }, [activeGroup]);

  const handleChildren = (key: RouteGroup) => {
    setOpened((st) => ({ ...st, [key]: !st[key] }));
  };

  const handleClick = (path: string) => {
    if (path === '//') {
      navigate_raw('/');
      return;
    }
    navigate_raw(path);
  };

  const getActive = (path: string) => {
    let active = false;
    if (path === '/') active = location === path;
    active = path === location || location.startsWith(`${path}/`);

    return cx(s.link, { [s.active]: active });
  };

  const renderLabelWithIcon = (label: string, icon?: IconList) => {
    return (
      <Fragment>
        <Icon className={s.icon} name={icon ?? 'circle_2'} sizeV2={1} />
        <label className={s.label}>{t(`lzy.${label}`)}</label>
      </Fragment>
    );
  };

  const renderMenu = (entry: NavRoute) => (
    <div key={entry.path} className={cx(s.entry, s.child)} onClick={sidebarOnClick}>
      <div className={getActive(entry.path)} onClick={() => handleClick(entry.path)}>
        {renderLabelWithIcon(entry.label ?? '', entry.icon)}
      </div>
    </div>
  );

  const renderGroup = (group: NavGroup) => {
    const routes = group.routes
      .filter((entry) => filterMenu(entry, authType, permissions))
      .filter((entry) => {
        if (!viewOnlyBookmarks) return true;
        return bookmarks.has(entry.path);
      });
    // return routes.length;
    if (!routes.length) return null;

    if (authType !== 'ADMIN' || viewOnlyBookmarks) {
      return routes.filter((entry) => Boolean(entry.label)).map(renderMenu);
    }

    const label = group.label ? `lzy.${group.label}` : `lzy.routes.group.${group.id}`;
    return (
      <div key={group.id} className={cx(s.entry, s.parent, s.section)}>
        <div className={s.wrapSectionLabel} onClick={() => handleChildren(group.id)}>
          <label className={cx(s.sectionLabel, { [s.active]: activeGroup === group.id })}>
            {t(label)}
          </label>
          <Icon
            name={opened[group.id] ? 'arrow_angle_up' : 'arrow_angle_down'}
            sizeV2={1}
            color={'text-l'}
          />
        </div>

        <AnimatePresence>
          {opened[group.id] && (
            <m.div
              className={cx(s.children, {
                [s.showChildren]: opened[group.id],
              })}
              initial='collapsed'
              animate='open'
              exit='collapsed'
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{
                type: 'tween',
                duration: 0.125,
                ease: [0.215, 0.61, 0.355, 1],
              }}
            >
              {routes.map(renderMenu)}
            </m.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div {...testId('menu')} className={cx(s.menu, className, { [s.shrunk]: shrunk })}>
      {navGroups.map(renderGroup)}
    </div>
  );
};

export default Menu;
